import UserNameDisplay from './UserNameDisplay';
import { SettingOutlined, LogoutOutlined, BellOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { NavLink, Link, useHistory } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Row, Col, Breadcrumb, Badge, Dropdown, Button, Avatar, message } from "antd";
import { supabase } from "../../supabaseClient";
import PropTypes from "prop-types";
import { useSupabaseUser, useAdminPhoto, useSupportMessages, usePendingPartnerRequestsCount } from "../../hooks/fetch";
import { getInitials } from "../../hooks";

const toggler = (
  <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
    <path d="M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z"/>
  </svg>
);

function paramMenu(history) {
  const handleParamMenuClick = async (key) => {
    if (key === "profile") {
      history.push("/Paramétres");
    } else if (key === "logout") {
      try {
        await supabase.auth.signOut();
        localStorage.removeItem("adminUser");
        window.location.href = '/sign-in';
      } catch {
        message.error('Erreur lors de la déconnexion');
      }
    }
  };

  return {
    onClick: ({ key }) => handleParamMenuClick(key),
    style: { padding: '4px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
    items: [
      {
        key: "profile",
        icon: <SettingOutlined style={{ fontSize: '16px' }} />,
        label: <Link to="/Paramétres">Paramétres</Link>,
        style: { borderRadius: '6px', margin: '2px 0', padding: '10px 16px', fontSize: '14px', color: '#141414' }
      },
      {
        key: "logout",
        icon: <LogoutOutlined style={{ fontSize: '16px' }} />,
        label: "Se déconnecter",
        style: { borderRadius: '6px', margin: '2px 0', padding: '10px 16px', fontSize: '14px', color: '#ff4d4f' },
        className: "logout-menu-item"
      }
    ]
  };
}

function Header({ name, subName, onPress }) {
  const routerHistory = useHistory();
  const { user, loading } = useSupabaseUser();
  const userId = user?.id || null;
  const photoUrl = useAdminPhoto(userId);

  const [adminProfile, setAdminProfile] = useState({ First_name: '', Last_name: '', role: '' });
  const [profileImage, setProfileImage] = useState('');
  const [adminLoaded, setAdminLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    (async () => {
      try {
        const [adminRes, profileRes] = await Promise.all([
          supabase
            .from('admin_Profile')
            .select('First_name, Last_name, role')
            .eq('user_id', userId)
            .maybeSingle(),
          supabase
            .from('profiles')
            .select('imageavatar')
            .eq('id', userId)
            .maybeSingle(),
        ]);
        if (!mounted) return;
        if (adminRes.data) setAdminProfile(adminRes.data);
        if (profileRes.data?.imageavatar) setProfileImage(profileRes.data.imageavatar);
      } catch {
        // silent
      } finally {
        if (mounted) setAdminLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  const isSousAdmin = String(adminProfile.role || '').toLowerCase() === 'sous admin';
  const canShowPartnerRequests = adminLoaded && !isSousAdmin;
  const avatarSrc = (profileImage && String(profileImage).trim()) || photoUrl || '';

  // ✅ Badge Realtime depuis support_messages
  const { unreadCount } = useSupportMessages();
  // ✅ Badge Realtime — demandes de partenariat en attente
  const { count: pendingPartnerRequests } = usePendingPartnerRequestsCount();

  const getDisplayLabel = (subNameProp, nameProp) => {
    const raw = (subNameProp || nameProp || '').toString();
    const uuidAnywhere = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}/g;
    let cleaned = raw.replace(uuidAnywhere, '').replace(/\//g, ' ').replace(/\s+/g, ' ').trim();
    if (!cleaned || /^\d{6,}$/.test(cleaned)) return 'Avis utilisateur';

    const labels = {
      'user-reviews': 'Avis utilisateur',
      'reviews-list': 'Tous les avis',
      'reviews-list-partner': 'Liste des avis - Partenaire',
      'reviews list partner': 'Liste des avis',
      'reviews': 'Tous les avis',
      'réclamations': 'Réclamations',
      'reclamations': 'Réclamations',
      'dashboard': 'Tableau de bord',
      'paramétres': 'Paramètres',
      'partenaires': 'Partenaires',
      'notifications': 'Notifications',
    };

    const fullKey = cleaned.toLowerCase();
    if (labels[fullKey]) return labels[fullKey];
    const key = fullKey.split(' ')[0];
    if (labels[key]) return labels[key];
    return cleaned.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  useEffect(() => {
    if (!loading && !userId) {
      routerHistory.push('/sign-in');
    }
  }, [loading, userId, routerHistory]);

  return (
    <Row gutter={[24, 0]}>
      <Col span={24} md={6}>
        <Breadcrumb
          items={[
            { title: <NavLink to="/Dashboard">BATILINK</NavLink> },
            { title: <span style={{ textTransform: 'capitalize' }}>{getDisplayLabel(subName, name)}</span> }
          ]}
        />
        <div className="ant-page-header-heading">
          <span className="ant-page-header-heading-title" style={{ textTransform: "capitalize" }}>
            {getDisplayLabel(subName, name)}
          </span>
        </div>
      </Col>

      <Col span={24} md={18} className="header-control">
        <Dropdown menu={paramMenu(routerHistory)} trigger={["click"]} placement="bottomRight">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginLeft: 12, cursor: 'pointer' }}>
            <Avatar
              size={36}
              src={avatarSrc || undefined}
              style={{
                border: '2px solid rgba(255,255,255,0.9)',
                background: !avatarSrc ? '#1e3a8a' : undefined,
                color: '#fff',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {!avatarSrc ? getInitials(adminProfile.First_name, adminProfile.Last_name) : null}
            </Avatar>
            <UserNameDisplay userId={userId || ''} />
          </div>
        </Dropdown>
                <button
          type="button"
          aria-label="Paramètres"
          onClick={() => routerHistory.push('/Paramétres')}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            fontSize: '20px',
            color: '#111827',
            marginLeft: '16px'

          }}
        >
          <SettingOutlined />
        </button>

        {/* ✅ Badge Realtime — demandes de partenariat en attente (super admin uniquement) */}
        {canShowPartnerRequests && (
          <Badge
            count={pendingPartnerRequests}
            size="small"
            style={{ backgroundColor: '#ef4444' }}
            overflowCount={99}
          >
            <button
              type="button"
              aria-label="Voir les demandes de partenariat"
              title="Demandes de partenariat"
              onClick={() => routerHistory.push('/Demandes')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: '20px',
                color: '#111827',
                marginLeft: '16px',
              }}
            >
              <UserSwitchOutlined />
            </button>
          </Badge>
        )}

        {/* ✅ Badge Realtime — se met à jour instantanément sans polling */}
        <Badge
          count={unreadCount}
          size="small"
          style={{ backgroundColor: '#ef4444' }}
          overflowCount={99}
        >
          <button
            type="button"
            aria-label="Voir les notifications"
            onClick={() => routerHistory.push('/Notifications')}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontSize: '20px',
              color: '#111827',
            }}
          >
            <BellOutlined />
          </button>
        </Badge>



        <Button type="link" className="sidebar-toggler" onClick={() => onPress()}>
          {toggler}
        </Button>
      </Col>
    </Row>
  );
}

Header.propTypes = {
  name: PropTypes.string,
  subName: PropTypes.string,
  onPress: PropTypes.func,
};

export default Header;